import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import DialogModal from '../../../components/dialogModal'
import QRCodeDownload from '../../../components/qrDownload'
import { NavBarCollapsableContext } from '../../../helpers/context'
import CreateNewFormLink from '../../../components/forms/links/formCreate'
import { AppDispatch, useAppDispatch } from '../../../redux/store'
import { createLink } from '../../../redux/reducers/urlReducer'
import { getToken, getTokenExpiration, logOut } from '../../../helpers/utils'
import { getInfo } from '../../../redux/reducers/userReducer'
import { CircularProgress, Typography } from '@mui/material'

type qrValues = {
  color: string
  logo: string
}

type utmValues = {
  origin: string
  media: string
  campaign: string
  term: string
  content: string
}
type LinkValues = {
  url: string
  domain: string
  title: string
  custom_code: string
  qr_code_checkBox: any
  utm_checkBox: any
  tags: string[]
  has_qr: boolean
  utm: utmValues
  qr_code: qrValues
}

interface Info {
  credits: {
    url: number
    qr: number
  }
  permissions: {
    custom_domain: boolean
    custom_post_domain: boolean
    has_qr: boolean
  }
  domains: string[]
  hasQrCredits: boolean
  hasUrlCredits: boolean
  plan: string
}

const CreateNewLink = () => {
  const dispatch: AppDispatch = useAppDispatch()
  const { t } = useTranslation()
  const classContext = useContext(NavBarCollapsableContext)
  const [openModal, setOpenModal] = useState(false)
  const [openModalPlan, setOpenModalPlan] = useState(false)
  let navigate = useNavigate()
  const [data, setData] = useState<any>({})
  const [shortUrlCreated, setShortUrlCreated] = useState(false)
  const [qrCodeCheked, setQrCodeCheked] = useState(false)
  const [goToPlans, setGoToPlans] = useState(false)
  const [userInfo, setUserInfo] = useState<Info>()
  const token = getToken()
  const sessionExpired = getTokenExpiration()
  const [showWait, setShowWait] = useState(false)

  useEffect(() => {
    if (sessionExpired) logOut()
  }, [sessionExpired])

  useEffect(() => {
    getUserInfo()
  }, [])

  const getUserInfo = async () => {
    try {
      const { payload } = await dispatch(
        getInfo({
          token: token,
        })
      )
      if (payload?.success) {
        setUserInfo(payload?.data)

        if (payload.data.credits.url === 0) navigate('/plans')
      } else {
        console.error(`${t('ERROR')}`)
      }
    } catch (error) {
      console.error('Error:', error)
    }
  }

  const handleSubmit = async (values: LinkValues) => {
    setShowWait(true)
    if (values.qr_code_checkBox) setQrCodeCheked(true)
    if (
      !values.utm.origin &&
      !values.utm.media &&
      !values.utm.campaign &&
      !values.utm.term &&
      !values.utm.content
    ) {
      values.utm_checkBox = false
    }

    const urlData = {
      url: values.url,
      domain: values.domain,
      title: values.title,
      custom_code: values.custom_code,
      has_qr: values.qr_code_checkBox,
      qr_code: values.qr_code_checkBox
        ? { color: values.qr_code.color, logo: values.qr_code.logo }
        : null,
      utm: values.utm_checkBox
        ? {
            origin: values.utm.origin,
            media: values.utm.media,
            campaign: values.utm.campaign,
            term: values.utm.term,
            content: values.utm.content,
          }
        : null,
    }

    try {
      const { payload } = await dispatch(createLink({ token, body: urlData }))

      if (payload && payload.success) {
        setShowWait(false)
        setShortUrlCreated(true)
        setData(payload?.data)
        setOpenModal(true)
        getUserInfo()
      } else {
        toast(payload.message)
      }
    } catch (error) {
      console.error('Error:', error)
    }
  }

  const handleCloseModal = () => {
    setOpenModal(false)
  }

  const handleCloseModalPlans = () => {
    setOpenModalPlan(false)
  }

  const copyLink = (short_url: string) => {
    navigator.clipboard.writeText(short_url)
    toast(`${t('LINK_COPIED')}`)
  }

  const handleGoToPlans = (canCreateQRCode: boolean) => {
    console.log('cancrewte', data)
    if (!canCreateQRCode) {
      setGoToPlans(true)
      setOpenModalPlan(true)
    }
  }

  return (
    <>
      <div className={`container-home background-white`}>
        <div className={classContext.classes.navbar_padding_left}>
          <span className='principal-title'>{`${t('CREATE_NEW')}`}</span>
          {showWait ? (
            <div className='circular-progress'>
              <CircularProgress size={50} color='info' />
              <Typography
                fontSize={'25px'}
                color={'#f47524'}
                fontWeight={'Bold'}
                variant='body1'
                style={{ marginTop: '20px' }}
              >
                {t('URL_IS_BEING_CREATED')}
              </Typography>
            </div>
          ) : (
            userInfo && (
              <CreateNewFormLink
                userInfo={userInfo}
                canCreateQRCode={handleGoToPlans}
                domains={userInfo?.domains || []}
                onSubmit={handleSubmit}
                shortUrlGenerated={data.short_url}
                url=''
                domain=''
                title=''
                custom_code=''
                has_qr={false}
                qr_code_checkBox={false}
                utm_checkBox={false}
                tags={[]}
                utm={{
                  origin: '',
                  media: '',
                  campaign: '',
                  term: '',
                  content: '',
                }}
                qr_code={{
                  color: '',
                  logo: '',
                }}
              />
            )
          )}
        </div>
      </div>

      {shortUrlCreated && (
        <DialogModal
          title={`${t('URL_CREATED')}`}
          open={openModal}
          handleClose={handleCloseModal}
          className='modal-top-red'
        >
          <div className='text-center my-4'>
            <div className='d-flex flex-column align-items-center'>
              <span className='span-short-url col-10'>{data.short_url}</span>
              <button
                disabled={false}
                onClick={() => {
                  copyLink(data.short_url)
                }}
                className='button-first my-4'
              >
                {`${t('COPY_LINK')}`}
              </button>
              <span
                className='color-grey-dark text-1rem pointer'
                dangerouslySetInnerHTML={{ __html: `${t('REVIEW_LINKS')}` }}
                onClick={() => navigate(`/links`)}
              />
            </div>
          </div>
          {qrCodeCheked && (
            <>
              <hr></hr>
              <div className='d-flex flex-column align-items-center'>
                <span className='modal-title'>{`${t('QR_CREATED')}`}</span>
                <QRCodeDownload qrData={data} />
              </div>
            </>
          )}
        </DialogModal>
      )}
      {goToPlans && (
        <DialogModal
          title={`${t('CHANGE_PLAN_QR')}`}
          open={openModalPlan}
          handleClose={handleCloseModalPlans}
          className='modal-top-red'
        >
          <div className='d-flex link-card-button-container'>
            <button
              disabled={false}
              onClick={() => navigate(`/plans`)}
              className='button-first my-4'
            >
              {`${t('ACCEPT')}`}
            </button>
            <button
              disabled={false}
              onClick={() => setOpenModalPlan(false)}
              className='button-seventh my-4'
            >
              {`${t('CLOSE')}`}
            </button>
          </div>
        </DialogModal>
      )}
    </>
  )
}

export default CreateNewLink
